import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  useCloneTemplateMutation,
  useGetTemplateQuery,
  useGetTemplateReferencesQuery,
  useUpdateTemplateMutation,
} from '../../../store/templateLibrary/templateLibrary'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthenticatedUser } from '../../../store/auth/authSlice'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { createPortal } from 'react-dom'
import { TemplateEditorHeader } from './TemplateEditorHeader'
import { TemplateEditorBox } from './TemplateEditorBox'
import { Col, Row, Spinner } from 'react-bootstrap'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { TemplateDetailsBox } from './TemplateDetailsBox'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { getTemplateLibraryTrackingDetails } from './tracking'
import { useAccount } from '../../../common/useAccount'
import { TemplateAIChat } from './TemplateAIChat'
import type { TemplateOrigin } from './templateOrigin'
import { TemplateDuplicateModal } from './TemplateDuplicateModal'
import { TemplateCloneRequest } from '../../../models/TemplateLibrary'
import { useCreateNodeMutation, useUpdateNodeMutation } from '../../../store/ai/inputParametersHierarchy'
import { calculateHierarchyId } from './utils'

export const TemplateEditor: React.FC = () => {
  const { tracked, trackingClient } = useTracking()
  const user = useAppSelector(selectAuthenticatedUser)
  const { portalRef } = useContext(HeaderContext)
  const { hasScope } = useZocksAuth()
  const hasTemplateAdminScope = hasScope('**', 'tmpl_lib:admin', ['tmpl_lib:admin'])
  const { accountId, account } = useAccount()
  const { templateId = '' } = useParams()
  const location = useLocation()
  const locationState = location.state as {
    aiAgentId?: string
    initialMessages?: Array<{ type: 'user' | 'agent'; message: string }>
    origin?: TemplateOrigin
  } | null
  const initialAiAgentId = locationState?.aiAgentId ?? null
  const initialMessages = locationState?.initialMessages
  const origin = locationState?.origin
  const [hasAIPendingChanges, setHasAIPendingChanges] = useState(false)
  const navigate = useNavigate()
  const { data: templateValue } = useGetTemplateQuery({ accountId, templateId })
  const { data: templateReferencesData } = useGetTemplateReferencesQuery({ accountId, templateId }, { skip: !templateId, refetchOnMountOrArgChange: true })
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateTemplateMutation()
  const [cloneTemplate, { isLoading: isCloning }] = useCloneTemplateMutation()
  const [createNode] = useCreateNodeMutation()
  const [updateNode] = useUpdateNodeMutation()
  const canEditTemplate =
    !account?.enterpriseConfig.lockTemplates &&
    !!templateValue &&
    templateValue.scope !== 'enterprise' &&
    (templateValue.scope === 'user' || templateValue.scope === 'system' || (templateValue.scope === 'account' && hasTemplateAdminScope))

  const [localTemplate, setLocalTemplate] = useState<string | null>(null)
  const [aiIsLoading, setAiIsLoading] = useState(false)
  const [pendingSaveContent, setPendingSaveContent] = useState<string | null>(null)
  const bypassBlockerRef = useRef(false)
  const currentEditorContentRef = useRef<string | null>(null)
  const handleEditorChange = useCallback((html: string) => {
    currentEditorContentRef.current = html
  }, [])
  const _handleTemplateChange = async (newTemplate: string): Promise<void> => {
    if (!templateValue) {
      return
    }
    await updateTemplate({
      accountId,
      templateId: templateValue.id,
      request: {
        template: {
          template: newTemplate,
        },
      },
    }).unwrap()
    setHasAIPendingChanges(false)
  }

  const handleDuplicateFromSystem = async (request: TemplateCloneRequest) => {
    if (!templateValue || pendingSaveContent === null) {
      return
    }
    const clone = await cloneTemplate({ accountId, templateId: templateValue.id, request }).unwrap()
    await updateTemplate({
      accountId,
      templateId: clone.id,
      request: { template: { template: pendingSaveContent } },
    }).unwrap()
    if (origin) {
      if (origin.existingNodeId) {
        await updateNode({
          accountId,
          type: origin.type,
          typeId: origin.typeId,
          nodeId: origin.existingNodeId,
          node: { templateId: clone.id },
        }).unwrap()
      } else {
        await createNode({
          accountId,
          type: origin.type,
          typeId: origin.typeId,
          node: { hierarchyId: calculateHierarchyId(origin.type, origin.context), templateId: clone.id },
        }).unwrap()
      }
    }
    setPendingSaveContent(null)
    bypassBlockerRef.current = true
    navigate(`/workspace/setup/templates/template/${clone.id}`, { replace: true, state: origin ? { origin } : undefined })
  }

  const handleTemplateChange = tracked(
    _handleTemplateChange,
    {
      eventName: TrackEvent.TEMPLATE_SAVED,
    },
    { condition: !!templateValue, eventDetailsExtractor: () => getTemplateLibraryTrackingDetails(templateValue!) },
  )

  const handleAITemplateUpdate = (newTemplate: string) => {
    setLocalTemplate(newTemplate)
    setHasAIPendingChanges(true)
  }

  useEffect(() => {
    setLocalTemplate(null)
    setAiIsLoading(false)
    bypassBlockerRef.current = false
    currentEditorContentRef.current = null
    setHasAIPendingChanges(false)
  }, [templateId])

  useEffect(() => {
    if (templateValue?.id && !user?.isGhosted) {
      trackingClient.track({
        eventName: TrackEvent.TEMPLATE_OPENED,
        eventDetails: getTemplateLibraryTrackingDetails(templateValue),
      })
    }
  }, [templateValue?.id, trackingClient, user?.isGhosted])

  return (
    <div className='truly-full-size-content'>
      {portalRef && portalRef.current ? createPortal(<TemplateEditorHeader templateName={templateValue?.name} />, portalRef.current) : null}
      {templateValue && templateReferencesData ? (
        <Row className='h-100'>
          <Col className='ps-3 pe-0' xs={8}>
            <TemplateEditorBox
              templateValue={localTemplate !== null ? { ...templateValue, template: { ...templateValue.template, template: localTemplate } } : templateValue}
              onSave={handleTemplateChange}
              onRequestDeferredSave={setPendingSaveContent}
              onChange={handleEditorChange}
              loading={isUpdating || isCloning}
              shouldReset={false}
              readOnly={!canEditTemplate || aiIsLoading}
              showDisabledBanner={!canEditTemplate}
              bypassBlockerRef={bypassBlockerRef}
              hasAIPendingChanges={hasAIPendingChanges}
            />
          </Col>
          <Col xs={4} className='zocks-bg-color-gray-100 border-start zocks-p-2.0 d-flex flex-column gap-3'>
            <TemplateDetailsBox templateValue={templateValue} readOnly={!canEditTemplate} origin={origin} />
            <TemplateAIChat
              templateId={templateId}
              currentTemplateRef={currentEditorContentRef}
              onTemplateUpdate={handleAITemplateUpdate}
              onLoadingChange={setAiIsLoading}
              readOnly={!canEditTemplate}
              initialAiAgentId={initialAiAgentId}
              initialMessages={initialMessages}
            />
          </Col>
        </Row>
      ) : (
        <div className='d-flex justify-content-center align-items-center h-100 pt-5'>
          <Spinner />
        </div>
      )}
      {pendingSaveContent !== null && templateValue ? (
        <TemplateDuplicateModal
          show={pendingSaveContent !== null}
          loading={isCloning || isUpdating}
          templateValue={templateValue}
          onDuplicate={handleDuplicateFromSystem}
          onCancel={() => setPendingSaveContent(null)}
        />
      ) : null}
    </div>
  )
}
